import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import type { ActionItem, FinancingView, InventoryInformationView, OverviewSummaryView } from "@slfinrtl/ui-bff-routes"
import { useCallback, useEffect, useState } from "react"
import { openListing } from "src/common/openListing/openListing"
import { addReferralToUrl } from "src/common/referralLink/addReferralToUrl"
import { Stage } from "src/components"
import { trackEvent } from "src/components/adobeAnalytics"
import { ErrorNotificationInline } from "src/context/errorHandler/ErrorNotificationInline"
import { withErrorHandler } from "src/context/errorHandler/withErrorHandler"
import { useModal } from "src/context/modal/useModal"
import type { OpenListingModalProps } from "src/hooks"
import { OpenListingModal, useConfig } from "src/hooks"
import type { VehicleDetailsStage } from "../../common/VehicleDetailsStage"
import { useVehicleDetailsContext } from "../../context/VehicleDetailsContext"
import { OverviewSummary } from "./components/overviewSummary/OverviewSummary"
import { OverviewSummarySkeleton } from "./components/overviewSummary/OverviewSummarySkeleton"
import { useColors } from "./hooks/useColors/useColors"

interface OverviewSummaryContainerProps {
	imageEdit?: ActionItem
	isFinancingEnabled: boolean
	onEditStageClick: (id: VehicleDetailsStage) => void
	view?: OverviewSummaryView
}

export const OverviewSummaryContainer = withErrorHandler((props: OverviewSummaryContainerProps) => {
	const { onEditStageClick, view } = props
	const [inventoryInformationView, setInventoryInformationView] = useState<InventoryInformationView | null>()
	const [financingView, setFinancingView] = useState<FinancingView>()

	const { subscribe } = useVehicleDetailsContext()
	const { getReferralId } = useDealerContext()
	const colorsProps = useColors()
	const { onlineOrderingEnabled } = useConfig()

	const showOpenListingModal = useModal<OpenListingModalProps>(OpenListingModal)

	const onOpenListing = useCallback(() => {
		trackEvent({ action: "click_on_open_listing_pics", category: "outlink" })
	}, [])

	const handleOpenGalleryClick = useCallback(async () => {
		const referralId = await getReferralId()
		if (!view) {
			return
		}

		const links = view.openGallery.links.map((link) => ({
			...link,
			url: addReferralToUrl(link.url, {
				int_id: "dealer_open_listing_referral_link",
				...(!link.url.includes("media") ? { mediaCenter: true } : {}),
				referral: referralId,
			}),
		}))

		openListing(links, showOpenListingModal, onOpenListing)
	}, [getReferralId, onOpenListing, showOpenListingModal, view])

	useEffect(() => {
		subscribe("inventoryInformationViewChanged", setInventoryInformationView)
		subscribe("financingViewChanged", setFinancingView)
	}, [subscribe])

	return (
		<Stage>
			<ErrorNotificationInline dismissButton={false} />
			{view ? (
				<OverviewSummary
					{...props}
					isOnlineOrderingEnabled={onlineOrderingEnabled}
					onEditClick={onEditStageClick}
					view={view}
					colors={colorsProps}
					inventoryInformationView={inventoryInformationView}
					financingView={financingView}
					onOpenGalleryClick={handleOpenGalleryClick}
				/>
			) : view === undefined ? (
				<OverviewSummarySkeleton />
			) : null}
		</Stage>
	)
})
